<script setup lang="ts">
import { computed, ref } from 'vue'
import '@/styles/care-card-title.css'
import applyToIcon from '@/assets/apply to.png'
import pceditIcon from '@/assets/pcedit.png'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import EditDefaultChecklist from '@/views/Librarypage/components/EditDefaultChecklist.vue'
import ApplyDefaultChecklist, {
  type SeasonOption,
} from '@/views/Librarypage/components/ApplyDefaultChecklist.vue'
import {
  loadDefaultChecklistPage,
  getDefaultChecklistList,
  type DefaultChecklistApplyTemplate,
  type DefaultChecklistMetaOption,
  type DefaultChecklistSeasonKey,
  type DefaultChecklistTemplate,
} from '@/api/defaultChecklist'
import { formatSeasonDisplayLabel } from '@/utils/seasons'

interface ChecklistItem {
  id: string
  item_id: string
  item_name: string
}

interface SeasonChecklist {
  key: DefaultChecklistSeasonKey
  title: string
  templateId: number | null
  items: ChecklistItem[]
}

const loading = ref(true)
const error = ref('')
const seasons = ref<SeasonChecklist[]>([])
const itemTypes = ref<DefaultChecklistMetaOption[]>([])
const applyTemplates = ref<DefaultChecklistApplyTemplate[]>([])

const selectedSeason = ref<string | null>(null)
const showEditModal = ref(false)
const editingSeasonKey = ref<DefaultChecklistSeasonKey | null>(null)
const showApplyModal = ref(false)
const applyingSeasonKey = ref<DefaultChecklistSeasonKey | null>(null)

const applyingSeason = computed(() => {
  if (!applyingSeasonKey.value) return null
  return seasons.value.find((s) => s.key === applyingSeasonKey.value) ?? null
})

const editingSeason = computed(() => {
  if (!editingSeasonKey.value) return null
  return seasons.value.find((s) => s.key === editingSeasonKey.value) ?? null
})

const editingSelectedCodes = computed(() => {
  return editingSeason.value?.items.map((item) => item.item_id) ?? []
})

const applySeasonOptions = computed<SeasonOption[]>(() => {
  if (applyTemplates.value.length > 0) {
    return applyTemplates.value.map((tpl) => {
      const season = seasons.value.find((s) => s.key === tpl.season_key)
      return {
        label: tpl.label,
        value: tpl.season_key,
        hasItems: (season?.items.length ?? 0) > 0,
        templateId: season?.templateId ?? tpl.value ?? tpl.id,
      }
    })
  }
  return seasons.value.map((season) => ({
    label: season.title,
    value: season.key,
    hasItems: season.items.length > 0,
    templateId: season.templateId,
  }))
})

function mapTemplateItems(template: DefaultChecklistTemplate): ChecklistItem[] {
  const baseline = template.baseline_items ?? []
  const routine = template.routine_items ?? []
  const split = [...baseline, ...routine]
  const source = split.length > 0 ? split : (template.items ?? [])
  return source.map((item) => ({
    id: String(item.id),
    item_id: item.item_id,
    item_name: item.item_name,
  }))
}

function parseSavedTemplate(data: unknown): DefaultChecklistTemplate | undefined {
  if (!data) return undefined
  if (typeof data === 'string') {
    try {
      return parseSavedTemplate(JSON.parse(data))
    } catch {
      return undefined
    }
  }
  if (typeof data !== 'object') return undefined
  const record = data as Record<string, unknown>
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    const nested = record.data as Record<string, unknown>
    if ('season_key' in nested || 'items' in nested || 'baseline_items' in nested) {
      return nested as unknown as DefaultChecklistTemplate
    }
  }
  if ('season_key' in record || 'items' in record || 'baseline_items' in record || 'id' in record) {
    return record as unknown as DefaultChecklistTemplate
  }
  return undefined
}

function normalizeSeasonCard(tpl: DefaultChecklistTemplate): SeasonChecklist {
  const rawTitle = tpl.season_name || tpl.template_name || tpl.season_key
  return {
    key: tpl.season_key,
    title: formatSeasonDisplayLabel(rawTitle) || rawTitle,
    templateId: tpl.id,
    items: mapTemplateItems(tpl),
  }
}

function applyListData(
  templates: DefaultChecklistTemplate[],
  options?: DefaultChecklistApplyTemplate[],
): void {
  if (options && options.length > 0) {
    applyTemplates.value = options.map((tpl) => ({
      ...tpl,
      label: formatSeasonDisplayLabel(tpl.label) || tpl.label,
    }))
  }
  seasons.value = templates.map((tpl) => normalizeSeasonCard(tpl))
}

function extractTemplates(data: unknown): DefaultChecklistTemplate[] {
  if (!data) return []
  if (Array.isArray(data)) return data as DefaultChecklistTemplate[]
  if (typeof data !== 'object') return []
  const record = data as Record<string, unknown>
  if (Array.isArray(record.list)) return record.list as DefaultChecklistTemplate[]
  if (record.data && typeof record.data === 'object') {
    const nested = record.data as Record<string, unknown>
    if (Array.isArray(nested.list)) return nested.list as DefaultChecklistTemplate[]
  }
  return []
}

function extractOptions(data: unknown): DefaultChecklistApplyTemplate[] | undefined {
  if (!data || typeof data !== 'object') return undefined
  const record = data as Record<string, unknown>
  if (Array.isArray(record.options)) return record.options as DefaultChecklistApplyTemplate[]
  return undefined
}

async function fetchPageData(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const { meta: metaRes, list: listRes } = await loadDefaultChecklistPage()

    if (metaRes?.code === 200 && metaRes.data) {
      itemTypes.value = metaRes.data.item_types ?? []
    }

    if (listRes?.code !== 200 || !listRes.data) {
      error.value = listRes?.msg || 'Failed to load default checklist'
      seasons.value = []
      return
    }

    const templates = extractTemplates(listRes.data)
    applyTemplates.value = (extractOptions(listRes.data) ?? []).map((tpl) => ({
      ...tpl,
      label: formatSeasonDisplayLabel(tpl.label) || tpl.label,
    }))
    seasons.value = templates.map((tpl) => normalizeSeasonCard(tpl))
  } catch {
    error.value = 'Network error, please try again later'
    seasons.value = []
  } finally {
    loading.value = false
  }
}

async function refreshList(
  savedTemplates: DefaultChecklistTemplate[] = [],
  fallback?: {
    seasonKey: DefaultChecklistSeasonKey | null
    templateId: number | null
    items: ChecklistItem[]
  },
): Promise<void> {
  try {
    const listRes = await getDefaultChecklistList({})
    if (listRes.code === 200 && listRes.data) {
      applyListData(extractTemplates(listRes.data), extractOptions(listRes.data))
    }
  } catch {
    // keep current cards
  }
  savedTemplates.forEach((tpl) => mergeTemplate(tpl))
  if (savedTemplates.length === 0 && fallback?.seasonKey) {
    patchSeasonItems(fallback.seasonKey, fallback.templateId, fallback.items)
  }
}

function handleCardClick(season: SeasonChecklist): void {
  selectedSeason.value = season.key
}

function handleEdit(season: SeasonChecklist): void {
  editingSeasonKey.value = season.key
  showEditModal.value = true
}

function handleCloseEditModal(): void {
  showEditModal.value = false
  editingSeasonKey.value = null
}

function patchSeasonItems(
  seasonKey: DefaultChecklistSeasonKey | null,
  templateId: number | null,
  items: ChecklistItem[],
): void {
  const index = seasons.value.findIndex(
    (season) =>
      (seasonKey != null && season.key === seasonKey) ||
      (templateId != null && season.templateId === templateId),
  )
  if (index === -1) return
  const current = seasons.value[index]
  if (!current) return
  seasons.value[index] = {
    ...current,
    items,
  }
}

function handleEditSaved(payload: {
  items: { id: string; item_id: string; item_name: string }[]
  template?: DefaultChecklistTemplate
}): void {
  const saved = parseSavedTemplate(payload.template) ?? payload.template
  const seasonKey = saved?.season_key || editingSeasonKey.value
  const templateId = saved?.id ?? editingSeason.value?.templateId ?? null

  if (saved) {
    mergeTemplate(saved)
  } else {
    patchSeasonItems(seasonKey, templateId, payload.items)
  }

  void refreshList(saved ? [saved] : [], {
    seasonKey,
    templateId,
    items: saved ? mapTemplateItems(saved) : payload.items,
  })
}

function handleApply(season: SeasonChecklist): void {
  applyingSeasonKey.value = season.key
  showApplyModal.value = true
}

function handleCloseApplyModal(): void {
  showApplyModal.value = false
  applyingSeasonKey.value = null
}

function mergeTemplate(tpl: DefaultChecklistTemplate): void {
  const next = normalizeSeasonCard(tpl)
  const index = seasons.value.findIndex(
    (season) => season.key === next.key || season.templateId === tpl.id,
  )
  if (index === -1) {
    if (!next.key) {
      patchSeasonItems(editingSeasonKey.value, tpl.id, next.items)
      return
    }
    seasons.value.push(next)
    return
  }
  const current = seasons.value[index]
  if (!current) return
  seasons.value[index] = {
    ...current,
    title: next.title || current.title,
    templateId: next.templateId ?? current.templateId,
    items: next.items,
  }
}

function handleApplied(payload: {
  targetSeasonKey: DefaultChecklistSeasonKey
  data?: { source?: DefaultChecklistTemplate; target?: DefaultChecklistTemplate }
}): void {
  const saved = [payload.data?.source, payload.data?.target].filter(
    (tpl): tpl is DefaultChecklistTemplate => Boolean(tpl),
  )
  saved.forEach((tpl) => mergeTemplate(tpl))
  void refreshList(saved)
}

void fetchPageData()
</script>

<template>
  <div class="default-checklist-container">
    <h1 class="page-title">Default Checklist</h1>

    <LoadingIndicator v-if="loading" text="Loading..." min-height="400px" />

    <div v-else-if="error" class="error-container">
      <p class="error-text">{{ error }}</p>
    </div>

    <div v-else class="templates-grid">
      <div
        v-for="season in seasons"
        :key="season.key"
        class="template-card"
        :class="{ active: selectedSeason === season.key }"
        @click="handleCardClick(season)"
      >
        <div class="card-header">
          <div class="card-title-section">
            <h3 class="card-title">{{ season.title }}</h3>
            <div class="card-actions">
              <button type="button" class="action-btn" @click.stop="handleApply(season)">
                <img :src="applyToIcon" alt="apply to" class="action-icon" />
              </button>
              <button type="button" class="action-btn" @click.stop="handleEdit(season)">
                <img :src="pceditIcon" alt="edit" class="action-icon" />
              </button>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div v-if="season.items.length > 0" class="checks-list">
            <div v-for="item in season.items" :key="item.id" class="check-item">
              <span class="check-id">{{ item.item_id }}</span>
              <span class="check-name">{{ item.item_name }}</span>
            </div>
          </div>
          <div v-else class="no-checks">No items</div>
        </div>
      </div>
    </div>

    <EditDefaultChecklist
      :visible="showEditModal"
      :season-title="editingSeason?.title || ''"
      :template-id="editingSeason?.templateId ?? null"
      :selected-item-codes="editingSelectedCodes"
      :item-types="itemTypes"
      @close="handleCloseEditModal"
      @saved="handleEditSaved"
    />

    <ApplyDefaultChecklist
      :visible="showApplyModal"
      :source-season-key="applyingSeasonKey"
      :source-template-id="applyingSeason?.templateId ?? null"
      :season-options="applySeasonOptions"
      @close="handleCloseApplyModal"
      @applied="handleApplied"
    />
  </div>
</template>

<style scoped>
.default-checklist-container {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0;
  box-sizing: border-box;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin-top: -10px;
  margin-left: -9px;
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.error-text {
  color: #ff4d4f;
  font-size: 14px;
}

.templates-grid {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 24px;
  padding: 0 0 24px;
  margin: 15px 0 0 -9px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  justify-content: flex-start;
}

.template-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s;
  height: 480px;
  width: 274px;
  flex: 0 0 274px;
  max-width: 274px;
  min-width: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.template-card.active {
  border-color: #666666;
  box-shadow: none;
}

.card-header {
  padding: 16px 16px 16px;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 20;
}

.card-title-section {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 0;
  padding-right: 56px;
}

.card-title {
  flex: 1;
}

.card-actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
  position: absolute;
  top: 18px;
  right: 16px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.card-body {
  flex: 1;
  padding: 0 0 16px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.card-body::-webkit-scrollbar {
  width: 6px;
}

.card-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.card-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.card-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.checks-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 16px;
}

.check-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.check-id {
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #999999;
}

.check-name {
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  word-break: break-word;
  white-space: normal;
  word-wrap: break-word;
}

.no-checks {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 13px;
}

@media (max-width: 320px) {
  .template-card {
    height: auto;
    min-height: 480px;
  }
}
</style>
